/**
 * @file timesheetImport.js
 * @description Pure, unit-tested engine for the tracker's CSV importer — the
 *   onboarding wedge that lets a therapist bring their history from any
 *   spreadsheet/competitor export instead of starting from zero.
 *
 *   Side-effect free: it takes already-parsed CSV rows (see importService.parseCsv),
 *   a column mapping, and the user's existing agencies/clients/services, and
 *   returns a validated "import plan" the service layer executes. No Supabase here.
 *
 *   What it does:
 *     • detectMapping(headers)   — propose which column is date/agency/client/etc.
 *     • buildImportPlan(...)     — per-row resolve + validate + dedup, plus the
 *       set of agencies/clients/services that would be auto-created, and a summary.
 *
 *   Resolution: agency/client/service names are matched to existing records by a
 *   normalized name (case/space/punctuation-insensitive) plus a small service
 *   alias map (Behavioral Treatment → Behavior Treatment, Family Training →
 *   Parent Training) consistent with the rest of the engine. Unmatched names are
 *   flagged for creation. Clients are agency-scoped, matched by initials.
 *
 *   PHI guard: client cells that look like a full name are rejected — the tracker
 *   is initials/codes only.
 *
 * @module lib/timesheetImport
 */
import { roundMoney, computeIncome, computeUnits, parseTimeInput } from "./timesheetTime";

export const IMPORT_FIELDS = ["date", "agency", "client", "service", "start", "end", "duration", "rate", "billable", "notes"];

// Header alias detection — substrings matched case-insensitively against the
// CSV's headers. First match wins. Covers our template + common exports
// (Clockify, Connecteam, generic spreadsheets).
const HEADER_ALIASES = {
  date:     ["date", "day", "session date", "start date"],
  agency:   ["agency", "payer", "company", "employer"],
  client:   ["client", "initials", "patient", "learner", "case"],
  service:  ["service", "session type", "service type", "activity", "task", "code", "description"],
  start:    ["start time", "start", "from", "time in", "clock in"],
  end:      ["end time", "end", "to", "time out", "clock out"],
  duration: ["duration", "hours", "hrs", "billable hours", "total hours", "time"],
  rate:     ["rate", "hourly rate", "pay rate", "rate/hr"],
  billable: ["billable"],
  notes:    ["notes", "note", "comment", "comments", "memo"],
};

// Expense import field set + aliases (reuses the same detector/parsers).
export const EXPENSE_FIELDS = ["date", "agency", "category", "amount", "notes"];
const EXPENSE_ALIASES = {
  date:     ["date", "day"],
  agency:   ["agency", "payer", "company", "employer", "project"],
  category: ["category", "type", "expense type", "expense", "kind"],
  amount:   ["amount", "cost", "total", "price", "expense amount"],
  notes:    ["notes", "note", "description", "comment", "memo", "details"],
};

/** Propose a {field: header|null} mapping from the CSV's header row. */
export function detectMapping(headers = [], fields = IMPORT_FIELDS, aliasMap = HEADER_ALIASES) {
  const norm = (h) => String(h || "").toLowerCase().trim();
  const mapping = {};
  const used = new Set();
  for (const field of fields) {
    const aliases = aliasMap[field] || [];
    let found = null;
    // Prefer exact-ish alias matches before substring to avoid "start date" → start.
    for (const alias of aliases) {
      const hit = headers.find((h) => !used.has(h) && norm(h) === alias);
      if (hit) { found = hit; break; }
    }
    if (!found) {
      for (const alias of aliases) {
        const hit = headers.find((h) => !used.has(h) && norm(h).includes(alias));
        if (hit) { found = hit; break; }
      }
    }
    mapping[field] = found || null;
    if (found) used.add(found);
  }
  return mapping;
}

/** Normalize a name for matching: lowercase, strip punctuation, collapse spaces. */
export function normalizeName(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

// Service-name aliases → our canonical catalog names (mirrors timesheetCalc).
const SERVICE_ALIASES = {
  "behavioral treatment": "Behavior Treatment",
  "behavior treatment": "Behavior Treatment",
  "family training": "Parent Training",
  "parent training": "Parent Training",
};
function canonicalService(name) {
  return SERVICE_ALIASES[normalizeName(name)] || String(name || "").trim();
}

/**
 * PHI guard: treat a value as a full name (reject) when it has 2+ alphabetic
 * words of 3+ letters. Initials/codes ("J.D.", "SaM", "AaL", "C12") pass.
 */
export function looksLikeFullName(value) {
  const parts = String(value || "").trim().split(/\s+/);
  if (parts.length < 2) return false;
  return parts.filter((p) => /^[A-Za-z]{3,}$/.test(p)).length >= 2;
}

/** Parse common date formats → YYYY-MM-DD, or null. */
export function parseDateFlexible(s) {
  const v = String(s || "").trim();
  if (!v) return null;
  let m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return iso(m[1], m[2], m[3]);
  m = v.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/); // M/D/Y (US)
  if (m) {
    let [, mo, d, y] = m;
    if (y.length === 2) y = "20" + y;
    return iso(y, mo, d);
  }
  return null;
}
function iso(y, mo, d) {
  const yy = Number(y), mm = Number(mo), dd = Number(d);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const probe = new Date(yy, mm - 1, dd);
  if (probe.getMonth() !== mm - 1 || probe.getDate() !== dd) return null; // rejects 02-30 etc.
  return `${yy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

function num(v) {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** Resolve duration in minutes from a row + mapping. */
function resolveDuration(get, mapping) {
  const startM = mapping.start ? parseTimeInput(get("start")) : null;
  const endM = mapping.end ? parseTimeInput(get("end")) : null;
  if (startM != null && endM != null && endM > startM) {
    return { minutes: endM - startM, startM, endM };
  }
  const raw = mapping.duration ? get("duration") : "";
  if (raw) {
    const str = String(raw).trim();
    if (str.includes(":")) {
      const [h, mi] = str.split(":").map(Number);
      if (Number.isFinite(h) && Number.isFinite(mi)) return { minutes: h * 60 + mi, startM, endM };
    }
    const n = num(str);
    if (n != null) {
      // Header wording decides hours vs minutes; default to hours (decimal),
      // the common export shape (e.g. Clockify "Duration (decimal)").
      const header = (mapping.duration || "").toLowerCase();
      const asMinutes = /min/.test(header) && !/hour|hr/.test(header);
      return { minutes: asMinutes ? Math.round(n) : Math.round(n * 60), startM, endM };
    }
  }
  return { minutes: null, startM, endM };
}

function dupKey(date, agencyNorm, clientNorm, serviceNorm, startM) {
  return `${date}|${agencyNorm}|${clientNorm}|${serviceNorm}|${startM ?? ""}`;
}

/**
 * Build the import plan.
 * @param {Object} args
 * @param {Array}  args.rows      - parsed CSV rows (objects keyed by header)
 * @param {Object} args.mapping   - {field: header}
 * @param {Array}  args.agencies  - existing tt_agencies
 * @param {Array}  args.clients   - existing tt_clients
 * @param {Array}  args.services  - existing tt_services
 * @param {Array}  [args.existingEntries] - existing tt_time_entries (for dedup)
 * @returns {Object} plan
 */
export function buildImportPlan({ rows = [], mapping = {}, agencies = [], clients = [], services = [], existingEntries = [] }) {
  const agencyByNorm = new Map(agencies.map((a) => [normalizeName(a.name), a]));
  const serviceByNorm = new Map(services.map((s) => [normalizeName(s.name), s]));
  // clients keyed by agencyId + normalized initials
  const clientKey = (agencyId, initials) => `${agencyId || ""}|${normalizeName(initials)}`;
  const clientByKey = new Map(clients.map((c) => [clientKey(c.agency_id, c.initials), c]));

  const existingDupKeys = new Set(
    existingEntries.map((e) => {
      const a = agencies.find((x) => x.id === e.agency_id);
      const c = clients.find((x) => x.id === e.client_id);
      const s = services.find((x) => x.id === e.service_id);
      return dupKey(e.date, normalizeName(a?.name), normalizeName(c?.initials), normalizeName(s?.name), e.start_minutes);
    })
  );

  const newAgencies = new Map();   // norm -> display name
  const newServices = new Map();   // norm -> canonical display name
  const newClients = new Map();    // `${agencyName}|${initials}` -> {initials, agencyName}
  const seenInBatch = new Set();

  const out = [];
  for (const row of rows) {
    const get = (field) => (mapping[field] ? (row[mapping[field]] ?? "") : "");
    const errors = [];

    const date = parseDateFlexible(get("date"));
    if (!date) errors.push(mapping.date ? "Invalid or missing date" : "No date column mapped");

    const agencyName = String(get("agency")).trim();
    if (!agencyName) errors.push("Missing agency");

    const clientRaw = String(get("client")).trim();
    if (!clientRaw) errors.push("Missing client");
    else if (looksLikeFullName(clientRaw)) errors.push("Client looks like a full name. Use initials only");

    const serviceName = canonicalService(get("service"));
    if (!serviceName) errors.push("Missing service");

    const { minutes, startM, endM } = resolveDuration(get, mapping);
    if (minutes == null || minutes <= 0) errors.push("No usable duration (need start+end or a duration)");

    // Rate: explicit column wins; else fall back to matched client/agency rate.
    let rate = mapping.rate ? num(get("rate")) : null;
    const matchedAgency = agencyByNorm.get(normalizeName(agencyName));
    const matchedService = serviceByNorm.get(normalizeName(serviceName));
    if (rate == null && matchedAgency && matchedService) {
      const matchedClient = clientByKey.get(clientKey(matchedAgency.id, clientRaw));
      rate =
        num(matchedClient?.service_rates?.[matchedService.id]) ??
        num(matchedClient?.hourly_rate) ??
        num(matchedAgency?.service_rates?.[matchedService.id]) ??
        0;
    }
    if (rate == null) rate = 0;

    const billableRaw = String(get("billable")).trim().toLowerCase();
    const billable = !(["false", "no", "n", "0"].includes(billableRaw));

    const duration = minutes || 0;
    const income = computeIncome(duration, rate);
    const units = computeUnits(duration);
    const notes = String(get("notes")).trim() || null;

    // Dedup (against existing + within this batch)
    const k = dupKey(date, normalizeName(agencyName), normalizeName(clientRaw), normalizeName(serviceName), startM);
    const isDup = !errors.length && (existingDupKeys.has(k) || seenInBatch.has(k));
    if (!errors.length) seenInBatch.add(k);

    // Track to-create records (only for rows that are otherwise valid)
    if (!errors.length && !isDup) {
      if (!agencyByNorm.has(normalizeName(agencyName))) newAgencies.set(normalizeName(agencyName), agencyName);
      if (!serviceByNorm.has(normalizeName(serviceName))) newServices.set(normalizeName(serviceName), serviceName);
      const matchedAg = agencyByNorm.get(normalizeName(agencyName));
      const cKey = matchedAg ? clientKey(matchedAg.id, clientRaw) : null;
      const clientExists = cKey ? clientByKey.has(cKey) : false;
      if (!clientExists) newClients.set(`${normalizeName(agencyName)}|${normalizeName(clientRaw)}`, { initials: clientRaw, agencyName });
    }

    out.push({
      rowIndex: row._id ?? out.length,
      status: errors.length ? "error" : isDup ? "duplicate" : "ok",
      errors,
      resolved: errors.length ? null : {
        date, agencyName, clientInitials: clientRaw, serviceName,
        start_minutes: startM, end_minutes: endM, duration_minutes: duration,
        units, rate_snapshot: rate, income, billable, notes,
      },
    });
  }

  const ok = out.filter((r) => r.status === "ok");
  const byAgency = {};
  for (const r of ok) byAgency[r.resolved.agencyName] = roundMoney((byAgency[r.resolved.agencyName] || 0) + r.resolved.income);
  const dates = ok.map((r) => r.resolved.date).sort();

  return {
    rows: out,
    newAgencies: [...newAgencies.values()],
    newServices: [...newServices.values()],
    newClients: [...newClients.values()],
    summary: {
      total: out.length,
      ok: ok.length,
      duplicates: out.filter((r) => r.status === "duplicate").length,
      errors: out.filter((r) => r.status === "error").length,
      totalIncome: roundMoney(ok.reduce((t, r) => t + r.resolved.income, 0)),
      byAgency,
      dateRange: dates.length ? { from: dates[0], to: dates[dates.length - 1] } : null,
    },
  };
}

/** Convenience: detect a mapping using the expense field set. */
export function detectExpenseMapping(headers = []) {
  return detectMapping(headers, EXPENSE_FIELDS, EXPENSE_ALIASES);
}

/**
 * Build an import plan for EXPENSES (date + amount required; agency optional and
 * auto-created; category/notes optional). Mirrors buildImportPlan's shape so the
 * wizard can render either kind.
 */
export function buildExpenseImportPlan({ rows = [], mapping = {}, agencies = [], existingExpenses = [] }) {
  const agencyByNorm = new Map(agencies.map((a) => [normalizeName(a.name), a]));
  const existDupKeys = new Set(
    existingExpenses.map((x) => {
      const a = agencies.find((g) => g.id === x.agency_id);
      return `${x.date}|${normalizeName(a?.name)}|${normalizeName(x.category)}|${Number(x.amount) || 0}`;
    })
  );
  const newAgencies = new Map();
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const get = (f) => (mapping[f] ? (row[mapping[f]] ?? "") : "");
    const errors = [];
    const date = parseDateFlexible(get("date"));
    if (!date) errors.push(mapping.date ? "Invalid or missing date" : "No date column mapped");
    const amount = num(get("amount"));
    if (amount == null || amount <= 0) errors.push("Missing or invalid amount");
    const agencyName = String(get("agency")).trim(); // optional — blank = general expense
    const category = String(get("category")).trim() || null;
    const notes = String(get("notes")).trim() || null;

    const dupK = `${date}|${normalizeName(agencyName)}|${normalizeName(category)}|${amount || 0}`;
    const isDup = !errors.length && (existDupKeys.has(dupK) || seen.has(dupK));
    if (!errors.length) seen.add(dupK);
    if (!errors.length && !isDup && agencyName && !agencyByNorm.has(normalizeName(agencyName))) {
      newAgencies.set(normalizeName(agencyName), agencyName);
    }

    out.push({
      rowIndex: row._id ?? out.length,
      status: errors.length ? "error" : isDup ? "duplicate" : "ok",
      errors,
      resolved: errors.length ? null : { date, agencyName: agencyName || null, category, amount, notes },
    });
  }
  const ok = out.filter((r) => r.status === "ok");
  const byAgency = {};
  for (const r of ok) {
    const k = r.resolved.agencyName || "(no agency)";
    byAgency[k] = roundMoney((byAgency[k] || 0) + r.resolved.amount);
  }
  const dates = ok.map((r) => r.resolved.date).sort();
  return {
    kind: "expenses",
    rows: out,
    newAgencies: [...newAgencies.values()],
    newClients: [],
    newServices: [],
    summary: {
      total: out.length,
      ok: ok.length,
      duplicates: out.filter((r) => r.status === "duplicate").length,
      errors: out.filter((r) => r.status === "error").length,
      totalAmount: roundMoney(ok.reduce((t, r) => t + r.resolved.amount, 0)),
      byAgency,
      dateRange: dates.length ? { from: dates[0], to: dates[dates.length - 1] } : null,
    },
  };
}
